/**
 * Build the streamChat handler set for one live assistant message. `patchLive`
 * applies a reducer to that message.
 *
 * The message is an ordered list of `parts` — {kind:'text', text} and
 * {kind:'tool', id, name, status} — appended in the order events arrive, so text
 * and tool rows interleave chronologically. `content` is kept in parallel as the
 * plain text (for the API transcript and history). `status` is the transient
 * "Thinking…" hint shown during gaps with no active tool.
 */

/** Append a text delta: extend the last text part, or start a new one. */
function pushText(parts, delta) {
    const list = parts || [];
    const last = list[list.length - 1];
    if (last && last.kind === 'text') {
        return [...list.slice(0, -1), {
            ...last,
            text: last.text + delta
        }];
    }
    return [...list, {
        kind: 'text',
        text: delta
    }];
}

/** The transcript sent to the model: user/assistant text turns only. */
export const toApiMessages = (list) => list
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({
        role: m.role,
        content: m.content
    }));

export function buildStreamHandlers(patchLive) {
    // `outcome` (attached to the returned handlers) records how the turn ended,
    // so the caller can decide whether to auto-continue.
    const outcome = {
        finishReason: null,
        hadTools: false,
        pendingWrites: [],
        pendingQuestions: [],
        finish: null,
        stalled: false
    };
    const handlers = {
        outcome,
        onText: (delta) => patchLive((msg) => ({
            ...msg,
            status: null,
            content: (msg.content || '') + (delta || ''),
            parts: pushText(msg.parts, delta || ''),
        })),
        // A tool is about to run — clear the "Thinking…" hint; the row is added on
        // tool-call (which carries the id we key on).
        onToolInputStart: () => patchLive((msg) => ({
            ...msg,
            status: null
        })),
        onToolCall: (evt) => {
            // Gated writes (auto-run off) surface as approval cards, not rows.
            if (evt.gated) return;
            outcome.hadTools = true;
            patchLive((msg) => ({
                ...msg,
                status: null,
                parts: [...(msg.parts || []), {
                    kind: 'tool',
                    id: evt.toolCallId,
                    name: evt.toolName,
                    detail: evt.detail || '',
                    status: 'running',
                }],
            }));
        },
        onToolResult: (evt) => patchLive((msg) => ({
            ...msg,
            parts: (msg.parts || []).map((p) => (p.kind === 'tool' && p.id === evt.toolCallId
                ? {
                    ...p,
                    status: 'done'
                } : p)),
        })),
        onDone: (evt) => {
            outcome.finishReason = evt.finishReason;
            outcome.pendingWrites = evt.pendingWrites || [];
            outcome.pendingQuestions = evt.pendingQuestions || [];
            outcome.finish = evt.finish || null;
            outcome.stalled = !!evt.stalled;
            patchLive((msg) => {
                // Empty turn (no text, no tools) → show a fallback instead of a blank bubble.
                const hasQuestion = (evt.pendingQuestions || []).length > 0;
                const empty = !hasQuestion && !msg.content && !(msg.parts || []).some((p) => p.kind === 'tool');
                return {
                    ...msg,
                    streaming: false,
                    status: null,
                    content: msg.content || (empty ? '_(No response — please try again.)_' : ''),
                    pendingWrites: evt.pendingWrites || [],
                    pendingQuestions: evt.pendingQuestions || [],
                };
            });
        },
        onError: (message) => patchLive((msg) => ({
            ...msg,
            streaming: false,
            status: null,
            content: msg.content ? `${msg.content}\n\n_[error: ${message}]_` : `Error: ${message}`,
            parts: pushText(msg.parts, `\n\n_[error: ${message}]_`),
        })),
    };
    return handlers;
}
